import { useCallback, useEffect, useState } from 'react';

/**
 * The state behind one "Bearbeiten → Speichern / Abbrechen" unit (F, PO
 * 2026-10-11; `docs/design-decisions.md` › Bearbeiten und Speichern).
 *
 * - The view shows the SAVED value; nothing can be changed before "Bearbeiten".
 * - "Bearbeiten" copies the saved value into a draft.
 * - `isDirty` compares the draft with the saved value — what Speichern is
 *   enabled by. Undoing a change by hand makes the unit clean again.
 * - "Abbrechen" drops the draft; a successful save calls `finish`, which closes
 *   the unit so the view shows the newly saved value.
 *
 * `setDraft` replaces the whole draft. Build the next value in ONE write per
 * event: two writes in one handler each start from the same draft, and the
 * second undoes the first.
 *
 * `equals` defaults to a JSON comparison, which is right for the plain config
 * objects these sections edit. Pass one when key order or formatting can
 * differ without the value differing.
 */
/** How long a just-saved value may stand in for the refetch, at most. */
export const JUST_SAVED_MS = 5000;

const jsonEquals = <T, >(a: T, b: T) => JSON.stringify(a) === JSON.stringify(b);

/** An open edit: the draft and the saved value it started from. */
export type DraftEdit<T> = { base: T, draft: T };

/**
 * Speichern's enabling condition: editing, and the draft differs from what it
 * started from. "Editing" is the edit object's presence, never the draft's
 * value — a draft may legitimately be `null` (e.g. "no issuer set").
 */
export const isDraftChanged = <T, >(
    edit: DraftEdit<T> | null,
    equals: (a: T, b: T) => boolean = jsonEquals,
): boolean => edit !== null && !equals(edit.draft, edit.base);

export const useEditableDraft = <T, >(
    saved: T,
    equals: (a: T, b: T) => boolean = jsonEquals,
) => {
    // The draft and the saved value it was opened from. Dirty is measured
    // against that base — what THIS person changed — not against the saved
    // value now: when another surface saves the same record meanwhile, the
    // unit must neither turn dirty by itself nor pretend the person changed
    // what they did not (callers can rebase such fields on save, see `base`).
    const [edit, setEdit] = useState<DraftEdit<T> | null>(null);
    const editing = edit !== null;
    // What a successful save stored, held only for the refetch window: the
    // mutation resolves before the cache refetch does, so without this the
    // closed unit would show the OLD value for a moment. It is DROPPED — not
    // just hidden — as soon as `saved` moves on, on Abbrechen, and at the
    // latest after JUST_SAVED_MS, so the view always falls back to what the
    // server returns: a refetch that fails, or a server that normalised the
    // value back, shows the server's value rather than ours. Dropping it also
    // keeps it from resurfacing for another tenant whose value happens to
    // equal the old one.
    const [justSaved, setJustSaved] = useState<{ value: T, over: T } | null>(null);
    const stale = justSaved !== null && !equals(saved, justSaved.over);
    useEffect(() => {
        if (stale) setJustSaved(null);
    }, [stale]);
    useEffect(() => {
        if (!justSaved) return undefined;
        const timer = setTimeout(() => setJustSaved(null), JUST_SAVED_MS);
        return () => clearTimeout(timer);
    }, [justSaved]);

    const shownSaved = justSaved && !stale ? justSaved.value : saved;

    const startEdit = useCallback(() => {
        setJustSaved(null);
        setEdit({ base: shownSaved, draft: shownSaved });
    }, [shownSaved]);
    const cancel = useCallback(() => {
        setJustSaved(null);
        setEdit(null);
    }, []);
    // Called after a successful save. By default the draft is what was stored;
    // pass `stored` when the save sent something else (e.g. a reset to null).
    const finish = useCallback((...stored: [] | [T]) => {
        if (edit) setJustSaved({ value: stored.length ? stored[0] as T : edit.draft, over: saved });
        setEdit(null);
    }, [edit, saved]);
    const setDraft = useCallback(
        (next: T) => setEdit((current) => (current ? { ...current, draft: next } : current)),
        [],
    );

    return {
        editing,
        /** The draft while editing, the saved value otherwise. */
        value: edit ? edit.draft : shownSaved,
        /** The saved value at "Bearbeiten" — what the draft started from. */
        base: edit ? edit.base : shownSaved,
        setDraft,
        isDirty: isDraftChanged(edit, equals),
        startEdit,
        cancel,
        finish,
    };
};

export default useEditableDraft;
